import { useEffect, useRef } from "react";

export const useModalAccessibility = ({ isOpen, onClose, modalRef }) => {
  const onCloseRef = useRef(onClose);
  const previousFocusRef = useRef(null);

  // Keep the callback ref up-to-date to avoid re-binding the event listener
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    // Capture the active element before the modal took over
    previousFocusRef.current = document.activeElement;

    // Focus the modal wrapper itself to reset the tab context
    // The element provided via modalRef MUST have tabIndex={-1}
    if (modalRef.current) {
      modalRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (!modalRef.current) return;

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (onCloseRef.current) {
          onCloseRef.current();
        }
        return;
      }

      if (e.key === "Tab") {
        const focusableElements = modalRef.current.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) {
          e.preventDefault();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement || document.activeElement === modalRef.current) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    // Use capture phase to ensure we trap before other elements handle it
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);

      // Restore focus to the original trigger when the modal unmounts/closes
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === "function") {
        setTimeout(() => {
          if (document.body.contains(previousFocusRef.current)) {
            previousFocusRef.current.focus();
          }
        }, 0);
      }
    };
  }, [isOpen, modalRef]);
};
